import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatInTimeZone } from "date-fns-tz";
import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SidePanel } from "@/components/ui/side-panel";
import { Switch } from "@/components/ui/switch";
import { stripeSupportedCountries } from "@/data/countries";
import { CohortsKeys, getAllCohorts } from "@/features/cohorts/api/cohorts";
import {
  AdminFoundersKeys,
  createAdminFounder,
  getAdminFounder,
  updateAdminFounder,
} from "@/features/founders/api/admin-founders";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  country: z.string().min(1, "Country is required"),
  cohort: z.string().optional(),
  isDownpayment: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

interface NewFounderDrawerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onClearFounderSelection: () => void;
  selectedFounderId: string | null;
}

const NewFounderDrawer: React.FC<NewFounderDrawerProps> = ({
  open,
  setOpen,
  onClearFounderSelection,
  selectedFounderId,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      country: "US",
      cohort: "",
      isDownpayment: true,
    },
  });

  // Get Founder for editing
  const { data: selectedFounder, isLoading: selectedFounderLoading } = useQuery(
    {
      queryKey: [AdminFoundersKeys.GET_FOUNDER, selectedFounderId],
      enabled: !!selectedFounderId,
      queryFn: () => getAdminFounder(selectedFounderId as string),
    },
  );

  // Get all cohorts
  const { data: cohorts = [] } = useQuery({
    queryKey: [CohortsKeys.GET_COHORTS],
    queryFn: getAllCohorts,
    enabled: open,
  });

  // Create Founder
  const { mutate: createMutation, isPending: createPending } = useMutation({
    mutationFn: createAdminFounder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      setOpen(false);
      form.reset();
      toast({ title: "Founder created successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error creating founder",
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  // Update Founder
  const { mutate: updateMutation, isPending: updatePending } = useMutation({
    mutationFn: (data: Partial<FormValues>) =>
      updateAdminFounder(selectedFounderId as string, {
        firstName: data.firstName,
        lastName: data.lastName,
        cohort: data.cohort,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [AdminFoundersKeys.GET_FOUNDERS],
      });
      onClearFounderSelection();
      toast({ title: "Founder updated successfully!" });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating founder",
        description: error?.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  // Populate form when editing
  React.useEffect(() => {
    if (selectedFounder) {
      form.reset({
        firstName: selectedFounder.firstName,
        lastName: selectedFounder.lastName,
        email: selectedFounder.email,
        country: selectedFounder.country,
        cohort:
          typeof selectedFounder.cohort === "string"
            ? selectedFounder.cohort
            : selectedFounder.cohort?._id || "",
        isDownpayment: selectedFounder.isDownpayment || false,
      });
    } else {
      form.reset({
        firstName: "",
        lastName: "",
        email: "",
        country: "US",
        cohort: "",
        isDownpayment: true,
      });
    }
  }, [selectedFounder, form]);

  // Auto-select the closest future cohort when creating a new founder
  React.useEffect(() => {
    if (!selectedFounderId && !form.getValues("cohort") && cohorts.length > 0) {
      const now = new Date().getTime();
      const upcomingCohorts = cohorts
        .filter((c) => {
          const start = new Date(c.startDate).getTime();
          return !Number.isNaN(start) && start >= now;
        })
        .sort(
          (a, b) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        );

      if (upcomingCohorts.length > 0) {
        form.setValue("cohort", upcomingCohorts[0]._id);
      }
    }
  }, [cohorts, selectedFounderId, form]);

  const onSubmit = (values: FormValues) => {
    if (selectedFounderId) {
      // Update founder
      updateMutation(values);
    } else {
      // Create founder
      const payload: any = {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        country: values.country,
      };

      // Include cohort only if selected (optional during creation)
      if (values.cohort) {
        payload.cohort = values.cohort;
      }

      // Include downpayment flag if enabled
      if (values.isDownpayment) {
        payload.isDownpayment = true;
      }

      createMutation(payload);
    }
  };

  const isPending = createPending || updatePending;

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClearFounderSelection}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="founder-form"
        disabled={isPending || !form.formState.isValid}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {selectedFounderId ? "Saving..." : "Creating..."}
          </>
        ) : (
          <>{selectedFounderId ? "Save" : "Create"}</>
        )}
      </Button>
    </div>
  );

  return (
    <SidePanel
      open={open}
      onOpenChange={onClearFounderSelection}
      title={selectedFounderId ? "Edit Founder" : "Create New Founder"}
      subtitle={
        selectedFounderId
          ? "Update founder information"
          : "Add a new founder to the system"
      }
      footer={footerContent}
    >
      {selectedFounderLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <Form {...form}>
          <form
            id="founder-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter first name"
                      autoComplete="given-name"
                      disabled={selectedFounderLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Enter last name"
                      autoComplete="family-name"
                      disabled={selectedFounderLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="Enter email address"
                      autoComplete="email"
                      disabled={!!selectedFounderId || selectedFounderLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!!selectedFounderId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {stripeSupportedCountries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cohort"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cohort</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a cohort" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {cohorts.map((cohort) => (
                        <SelectItem key={cohort._id} value={cohort._id}>
                          #{cohort.number}:{" "}
                          {formatInTimeZone(
                            cohort.startDate,
                            "America/New_York",
                            "MMMM d, yyyy",
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Downpayment toggle - shown when creating new founder */}
            {!selectedFounderId && (
              <FormField
                control={form.control}
                name="isDownpayment"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-base">Downpayment</FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={selectedFounderLoading}
                        />
                      </FormControl>
                    </div>
                    <FormDescription>
                      Enable if this founder requires a downpayment
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </form>
        </Form>
      )}
    </SidePanel>
  );
};

export default NewFounderDrawer;
